import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { gmDisplayName } from '../utils/displayName';
import { useTranslation } from 'react-i18next';
import { awardsAPI, playersAPI } from '../api/api';
import { useAuth } from '../context/AuthContext';
import PlayerAvatar from '../components/PlayerAvatar';

// ─────────────────────────────────────────────
// Config
// ─────────────────────────────────────────────
const STAT_LEADER_META = [
  { category: 'scoring_leader',  emoji: '🏀', label: 'Scoring Champion',  statKey: 'ppg', statLabel: 'PPG' },
  { category: 'rebounds_leader', emoji: '💪', label: 'Rebounds Leader',    statKey: 'rpg', statLabel: 'RPG' },
  { category: 'assists_leader',  emoji: '🎯', label: 'Assists Leader',     statKey: 'apg', statLabel: 'APG' },
  { category: 'blocks_leader',   emoji: '🚫', label: 'Blocks Leader',      statKey: 'bpg', statLabel: 'BPG' },
  { category: 'steals_leader',   emoji: '⚡', label: 'Steals Leader',      statKey: 'spg', statLabel: 'SPG' },
];

const CATEGORY_META = {
  mvp:  { emoji: '🏆', label: 'Most Valuable Player',       short: 'MVP',  winnerType: 'player' },
  dpoy: { emoji: '🛡️', label: 'Defensive Player of the Year', short: 'DPOY', winnerType: 'player' },
  gmoy: { emoji: '🧠', label: 'GM of the Year',              short: 'GMOY', winnerType: 'gm'     },
  mip:  { emoji: '📈', label: 'Most Improved Player',        short: 'MIP',  winnerType: 'player' },
  roty: { emoji: '🌟', label: 'Rookie of the Year',          short: 'ROTY', winnerType: 'player' },
};
const CATEGORIES = ['mvp', 'dpoy', 'gmoy', 'mip', 'roty'];

// ─────────────────────────────────────────────
// Stat columns per category
// ─────────────────────────────────────────────
const PLAYER_COLS = [
  { key: 'games', label: 'G' },
  { key: 'ppg',    label: 'PPG' },
  { key: 'rpg',    label: 'RPG' },
  { key: 'apg',    label: 'APG' },
  { key: 'spg',    label: 'SPG' },
  { key: 'bpg',    label: 'BPG' },
  { key: 'fg_pct', label: 'FG%' },
  { key: 'score',  label: 'Score' },
];
const DPOY_COLS = [
  { key: 'games',  label: 'G' },
  { key: 'ppg',    label: 'PPG' },
  { key: 'rpg',    label: 'RPG' },
  { key: 'spg',    label: 'SPG' },
  { key: 'bpg',    label: 'BPG' },
  { key: 'stocks', label: 'Stocks' },
  { key: 'score',  label: 'Score' },
];
const GMOY_COLS = [
  { key: 'wins',    label: 'W' },
  { key: 'total_games', label: 'GP' },
  { key: 'win_pct', label: 'Win%' },
  { key: 'trades',  label: 'Trades' },
  { key: 'score',   label: 'Score' },
];
const MIP_COLS = [
  { key: 'games',    label: 'G' },
  { key: 'prev_ppg', label: 'Prev PPG' },
  { key: 'ppg',      label: 'PPG' },
  { key: 'prev_rpg', label: 'Prev RPG' },
  { key: 'rpg',      label: 'RPG' },
  { key: 'score',    label: 'Δ Score' },
];

function getCols(category) {
  if (category === 'dpoy') return DPOY_COLS;
  if (category === 'gmoy') return GMOY_COLS;
  if (category === 'mip')  return MIP_COLS;
  return PLAYER_COLS;
}

function candidateName(c, category) {
  if (category === 'gmoy') return gmDisplayName(c, { preferFirstName: true });
  return c.player;
}

function candidateId(c, category) {
  return category === 'gmoy' ? c.userId : c.playerId;
}

function candidateTeam(c) {
  return c.team;
}

// ─────────────────────────────────────────────
// Stat Leaders Section
// ─────────────────────────────────────────────
function StatLeadersSection({ statLeaders, playoffsStarted, statLeadersLocked, isAdmin, seasonId, onRefresh }) {
  const [locking, setLocking] = useState(false);
  const [lockError, setLockError] = useState(null);

  if (!statLeaders) return null;
  const hasAny = STAT_LEADER_META.some(m => statLeaders[m.category]);
  if (!hasAny) return null;

  const handleLock = async () => {
    setLocking(true); setLockError(null);
    try {
      await awardsAPI.lockStatLeaders(seasonId);
      await onRefresh();
    } catch (e) {
      setLockError(e.response?.data?.error || 'Failed to lock');
    } finally {
      setLocking(false);
    }
  };

  return (
    <div className="mb-10">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">
          {statLeadersLocked ? '🏆 Regular Season Champions' : '📊 Regular Season Leaders'}
        </h2>
        {!statLeadersLocked && (
          <span className="text-xs font-medium bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-2.5 py-1 rounded-full">
            ⏳ Not yet locked
          </span>
        )}
        {statLeadersLocked && (
          <span className="text-xs font-medium bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300 px-2.5 py-1 rounded-full">
            🔒 Final
          </span>
        )}
        {isAdmin && !statLeadersLocked && (
          <button
            onClick={handleLock}
            disabled={locking}
            className="ml-auto text-xs px-3 py-1.5 rounded-lg bg-yellow-500 hover:bg-yellow-600 text-white font-semibold transition disabled:opacity-50"
          >
            {locking ? 'Locking…' : '🔒 Lock Stat Leaders'}
          </button>
        )}
        {lockError && <span className="text-xs text-red-500">{lockError}</span>}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {STAT_LEADER_META.map(({ category, emoji, label, statKey, statLabel }) => {
          const leader = statLeaders[category];
          if (!leader) return (
            <div key={category}
              className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 text-center border-2 border-dashed border-gray-200 dark:border-gray-700 opacity-50">
              <div className="text-2xl mb-1">{emoji}</div>
              <div className="text-xs font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">{label}</div>
              <div className="text-sm text-gray-400 mt-3">No data</div>
            </div>
          );

          const firstName = leader.player.split(' ')[0];
          const lastName  = leader.player.split(' ').slice(1).join(' ');

          return (
            <div key={category}
              className={`bg-white dark:bg-gray-800 rounded-xl shadow p-4 text-center border-2 transition
                ${statLeadersLocked
                  ? 'border-yellow-400 dark:border-yellow-500'
                  : 'border-gray-200 dark:border-gray-700'}`}>
              <div className="text-xl mb-0.5">{emoji}</div>
              <div className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2 leading-tight">
                {label}
              </div>

              <PlayerAvatar
                player={{ imageUrl: leader.imageUrl, firstName, lastName }}
                size="medium"
                className="mx-auto mb-2"
              />

              <div className="text-sm font-bold text-gray-800 dark:text-gray-100 leading-tight truncate">
                {leader.player}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-2 truncate">{leader.team}</div>

              <div className={`text-xl font-bold ${statLeadersLocked ? 'text-yellow-600 dark:text-yellow-400' : 'text-blue-600 dark:text-blue-400'}`}>
                {leader[statKey]}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">{statLabel}</div>

              {statLeadersLocked && (
                <div className="mt-2 text-xs font-semibold text-yellow-600 dark:text-yellow-400">👑 Champion</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Nominee Picker Modal (admin — opens voting)
// ─────────────────────────────────────────────
function NomineePickerModal({ category, candidates, seasonId, onClose, onRefresh }) {
  const meta = CATEGORY_META[category];
  const isGmoy = category === 'gmoy';

  // Build nominee shape from a candidate
  const toNominee = (c) => isGmoy
    ? { userId: c.userId, firstName: c.firstName, gamertag: c.gamertag, team: c.team, logoUrl: c.logoUrl }
    : { playerId: c.playerId, player: c.player, team: c.team, imageUrl: c.imageUrl };

  const nomineeId = (n) => isGmoy ? n.userId : n.playerId;
  const nomineeName = (n) => isGmoy ? gmDisplayName(n, { preferFirstName: true }) : n.player;

  // Pre-select top 5 from candidates
  const [selected, setSelected] = useState(() => candidates.slice(0, 5).map(toNominee));
  const [search, setSearch] = useState('');
  const [allPlayers, setAllPlayers] = useState([]);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Load full player list for search (player categories only)
  useEffect(() => {
    if (isGmoy) return;
    setLoadingPlayers(true);
    playersAPI.getAll()
      .then(res => setAllPlayers(res.data || []))
      .catch(() => {})
      .finally(() => setLoadingPlayers(false));
  }, [isGmoy]);

  const selectedIds = useMemo(() => new Set(selected.map(nomineeId)), [selected]);

  const toggle = (nominee) => {
    const id = nomineeId(nominee);
    if (selectedIds.has(id)) {
      setSelected(prev => prev.filter(n => nomineeId(n) !== id));
    } else {
      setSelected(prev => [...prev, nominee]);
    }
  };

  // For player search: filter allPlayers not already in candidates
  const searchResults = useMemo(() => {
    if (!search.trim() || isGmoy) return [];
    const q = search.toLowerCase();
    return allPlayers
      .filter(p => {
        const name = `${p.firstName} ${p.lastName}`.toLowerCase();
        return name.includes(q) && !candidates.some(c => c.playerId === p.id);
      })
      .slice(0, 6)
      .map(p => ({ playerId: p.id, player: `${p.firstName} ${p.lastName}`, team: p.team?.name || '', imageUrl: p.imageUrl }));
  }, [search, allPlayers, candidates, isGmoy]);

  const handleSubmit = async () => {
    if (selected.length < 2) { setError('Select at least 2 nominees'); return; }
    setSubmitting(true); setError(null);
    try {
      await awardsAPI.openVoting({ seasonId, category, nominees: selected });
      await onRefresh();
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to open voting');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100">
            {meta.emoji} Set Nominees — {meta.short}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Pick who goes on the ballot. GMs will vote from this list.
          </p>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-5">

          {/* Top candidates — quick toggle chips */}
          <div>
            <div className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
              Top candidates (click to add/remove)
            </div>
            <div className="flex flex-wrap gap-2">
              {candidates.map(c => {
                const id = isGmoy ? c.userId : c.playerId;
                const name = isGmoy ? gmDisplayName(c, { preferFirstName: true }) : c.player;
                const isOn = selectedIds.has(id);
                return (
                  <button
                    key={id}
                    onClick={() => toggle(toNominee(c))}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition border
                      ${isOn
                        ? 'bg-nba-blue border-nba-blue text-white'
                        : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-nba-blue hover:text-nba-blue dark:hover:border-blue-400 dark:hover:text-blue-400'}`}
                  >
                    {isOn ? '✓' : '+'} {name}
                    {!isGmoy && <span className="opacity-60 text-xs ml-1">{c.ppg} PPG</span>}
                    {isGmoy && <span className="opacity-60 text-xs ml-1">{c.win_pct}%</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Search any player (non-GMOY only) */}
          {!isGmoy && (
            <div>
              <div className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                Search any player
              </div>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Type player name…"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm
                  bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100
                  focus:outline-none focus:ring-2 focus:ring-nba-blue"
              />
              {loadingPlayers && <p className="text-xs text-gray-400 mt-1">Loading players…</p>}
              {searchResults.length > 0 && (
                <div className="mt-2 space-y-1">
                  {searchResults.map(p => {
                    const isOn = selectedIds.has(p.playerId);
                    return (
                      <div key={p.playerId}
                        className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
                        <div className="flex items-center gap-2">
                          {p.imageUrl && (
                            <img src={p.imageUrl} alt={p.player}
                              className="w-7 h-7 rounded-full object-cover bg-gray-200"
                              onError={e => { e.target.style.display='none'; }} />
                          )}
                          <div>
                            <div className="text-sm font-semibold text-gray-800 dark:text-gray-100">{p.player}</div>
                            <div className="text-xs text-gray-500">{p.team}</div>
                          </div>
                        </div>
                        <button
                          onClick={() => toggle(p)}
                          className={`text-xs px-2 py-1 rounded font-semibold transition
                            ${isOn ? 'bg-red-100 text-red-600 hover:bg-red-200' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}>
                          {isOn ? 'Remove' : '+ Add'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Selected ballot preview */}
          <div>
            <div className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
              Ballot ({selected.length} selected)
            </div>
            {selected.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No nominees selected yet</p>
            ) : (
              <div className="space-y-1">
                {selected.map((n, i) => (
                  <div key={nomineeId(n)}
                    className="flex items-center justify-between px-3 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                      {i + 1}. {nomineeName(n)}
                      <span className="ml-1.5 text-xs font-normal text-gray-500">{n.team}</span>
                    </span>
                    <button
                      onClick={() => setSelected(prev => prev.filter(x => nomineeId(x) !== nomineeId(n)))}
                      className="text-gray-400 hover:text-red-500 transition text-lg leading-none">×</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && <p className="text-red-500 text-sm">{error}</p>}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex gap-3">
          <button onClick={onClose}
            className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition text-sm">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || selected.length < 2}
            className="flex-1 px-4 py-2 rounded-lg bg-nba-blue hover:bg-blue-800 text-white font-semibold transition text-sm disabled:opacity-50">
            {submitting ? 'Opening…' : `🗳️ Open Voting (${selected.length} nominees)`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Modals
// ─────────────────────────────────────────────
function AnnounceModal({ category, candidates, onConfirm, onClose }) {
  const meta = CATEGORY_META[category];
  const isGmoy = category === 'gmoy';
  const [selected, setSelected] = useState(candidates[0] ? candidateId(candidates[0], category) : null);
  const [selectedName, setSelectedName] = useState(candidates[0] ? candidateName(candidates[0], category) : '');
  const [notes, setNotes] = useState('');
  const [search, setSearch] = useState('');
  const [allPlayers, setAllPlayers] = useState([]);

  // Load full player list for search (player categories only)
  useEffect(() => {
    if (isGmoy) return;
    playersAPI.getAll().then(res => setAllPlayers(res.data || [])).catch(() => {});
  }, [isGmoy]);

  const searchResults = useMemo(() => {
    if (!search.trim() || isGmoy) return [];
    const q = search.toLowerCase();
    return allPlayers
      .filter(p => `${p.firstName} ${p.lastName}`.toLowerCase().includes(q))
      .slice(0, 6);
  }, [search, allPlayers, isGmoy]);

  const pick = (id, name) => { setSelected(id); setSelectedName(name); setSearch(''); };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100">
            {meta.emoji} Announce {meta.short} Winner
          </h3>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
          {/* Top candidates quick-pick */}
          {candidates.length > 0 && (
            <div>
              <div className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                Top candidates
              </div>
              <div className="space-y-1.5">
                {candidates.map(c => {
                  const id = candidateId(c, category);
                  const name = candidateName(c, category);
                  return (
                    <label key={id}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border-2 transition
                        ${selected === id
                          ? 'border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'}`}
                    >
                      <input type="radio" name="winner" value={id}
                        checked={selected === id}
                        onChange={() => pick(id, name)}
                        className="accent-yellow-500"
                      />
                      <div>
                        <div className="font-semibold text-gray-800 dark:text-gray-100">{name}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{candidateTeam(c)}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* Search any player (non-GMOY) */}
          {!isGmoy && (
            <div>
              <div className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                Or search any player
              </div>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Type player name…"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm
                  bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100
                  focus:outline-none focus:ring-2 focus:ring-yellow-400"
              />
              {searchResults.length > 0 && (
                <div className="mt-2 space-y-1">
                  {searchResults.map(p => {
                    const fullName = `${p.firstName} ${p.lastName}`;
                    return (
                      <div key={p.id}
                        onClick={() => pick(p.id, fullName)}
                        className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer border-2 transition
                          ${selected === p.id
                            ? 'border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20'
                            : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                      >
                        {p.imageUrl && (
                          <img src={p.imageUrl} alt={fullName}
                            className="w-8 h-8 rounded-full object-cover bg-gray-200"
                            onError={e => { e.target.style.display='none'; }} />
                        )}
                        <div>
                          <div className="text-sm font-semibold text-gray-800 dark:text-gray-100">{fullName}</div>
                          <div className="text-xs text-gray-500">{p.team?.name || ''}</div>
                        </div>
                        {selected === p.id && <span className="ml-auto text-yellow-500 font-bold">✓</span>}
                      </div>
                    );
                  })}
                </div>
              )}
              {selected && !candidates.some(c => candidateId(c, category) === selected) && selectedName && (
                <div className="mt-2 px-3 py-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-lg text-sm font-semibold text-gray-800 dark:text-gray-100">
                  ✓ Selected: {selectedName}
                </div>
              )}
            </div>
          )}

          {/* Notes */}
          <div>
            <div className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
              Notes (optional)
            </div>
            <textarea
              placeholder="e.g. 'Dominant all-round season'"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2 text-sm
                bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 resize-none focus:outline-none focus:ring-2 focus:ring-yellow-400"
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex gap-3">
          <button onClick={onClose}
            className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition text-sm">
            Cancel
          </button>
          <button
            onClick={() => selected && onConfirm(selected, meta.winnerType, notes)}
            disabled={!selected}
            className="flex-1 px-4 py-2 rounded-lg bg-yellow-500 hover:bg-yellow-600 text-white font-semibold transition disabled:opacity-50 text-sm">
            🏅 Announce Winner
          </button>
        </div>
      </div>
    </div>
  );
}

function VoteModal({ category, candidates, onConfirm, onClose, userTeamId }) {
  const meta = CATEGORY_META[category];
  const eligible = candidates.filter(c => {
    if (category === 'gmoy') return c.userId !== userTeamId; // userId check done server-side too
    return c.teamId !== userTeamId;
  });
  const [selected, setSelected] = useState(eligible[0] ? candidateId(eligible[0], category) : null);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md p-6">
        <h3 className="text-xl font-bold mb-1 text-gray-800 dark:text-gray-100">
          {meta.emoji} Vote — {meta.short}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Your vote is private until admin closes voting.</p>
        <div className="space-y-2 mb-6">
          {eligible.map(c => {
            const id = candidateId(c, category);
            return (
              <label key={id}
                className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border-2 transition
                  ${selected === id
                    ? 'border-nba-blue bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'}`}
              >
                <input type="radio" name="vote" value={id}
                  checked={selected === id}
                  onChange={() => setSelected(id)}
                  className="accent-blue-600"
                />
                <div>
                  <div className="font-semibold text-gray-800 dark:text-gray-100">{candidateName(c, category)}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">{candidateTeam(c)}</div>
                </div>
              </label>
            );
          })}
        </div>
        <div className="flex gap-3">
          <button onClick={onClose}
            className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition">
            Cancel
          </button>
          <button
            onClick={() => selected && onConfirm(selected, meta.winnerType)}
            disabled={!selected}
            className="flex-1 px-4 py-2 rounded-lg bg-nba-blue hover:bg-blue-800 text-white font-semibold transition disabled:opacity-50">
            Submit Vote
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Winner Spotlight
// ─────────────────────────────────────────────
function WinnerSpotlight({ winner, candidates, category }) {
  const meta = CATEGORY_META[category];
  const wId = winner.id;
  const winnerData = candidates.find(c => candidateId(c, category) === wId || candidateId(c, category) === parseInt(wId));
  if (!winnerData) return null;

  return (
    <div className="mb-5 rounded-xl border-2 border-yellow-400 bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 p-4 flex items-center gap-4">
      <div className="text-4xl">🥇</div>
      <div className="flex-1">
        <div className="text-xs font-bold text-yellow-600 dark:text-yellow-400 uppercase tracking-wider mb-0.5">
          {meta.short} Winner
        </div>
        <div className="text-xl font-bold text-gray-800 dark:text-gray-100">{candidateName(winnerData, category)}</div>
        <div className="text-sm text-gray-500 dark:text-gray-400">{candidateTeam(winnerData)}</div>
        {winner.notes && <div className="text-sm italic text-gray-600 dark:text-gray-300 mt-1">"{winner.notes}"</div>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Candidate row
// ─────────────────────────────────────────────
function CandidateRow({ candidate, category, rank, voteSummary, totalVotes, myVote, votingStatus, isWinner }) {
  const cols = getCols(category);
  const id = candidateId(candidate, category);
  const voteCount = voteSummary ? (voteSummary[id] || 0) : null;
  const isMyVote = myVote === id || myVote === parseInt(id);

  return (
    <div className={`flex items-center gap-3 py-2.5 px-3 rounded-lg transition
      ${isWinner ? 'bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-300 dark:border-yellow-700' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'}
      ${isMyVote && votingStatus === 'open' ? 'ring-1 ring-nba-blue' : ''}`}
    >
      {/* Rank */}
      <div className="w-5 text-center text-sm font-bold text-gray-400 dark:text-gray-500 shrink-0">{rank}</div>

      {/* Avatar + name */}
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {candidate.imageUrl && (
          <img src={candidate.imageUrl} alt={candidateName(candidate, category)}
            className="w-8 h-8 rounded-full object-cover shrink-0 bg-gray-200 dark:bg-gray-700"
            onError={e => { e.target.style.display = 'none'; }}
          />
        )}
        <div className="min-w-0">
          <div className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">
            {candidateName(candidate, category)}
            {isMyVote && votingStatus === 'open' && <span className="ml-1.5 text-xs text-nba-blue">✓ Your vote</span>}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{candidateTeam(candidate)}</div>
        </div>
      </div>

      {/* Stats */}
      <div className="hidden sm:flex gap-3 shrink-0">
        {cols.map(col => (
          <div key={col.key} className="text-center w-12">
            <div className="text-xs text-gray-500 dark:text-gray-400">{col.label}</div>
            <div className="text-sm font-semibold text-gray-800 dark:text-gray-100">{candidate[col.key] ?? '—'}</div>
          </div>
        ))}
      </div>

      {/* Vote bar (after voting closed/announced) */}
      {voteSummary && votingStatus === 'announced' && (
        <div className="shrink-0 text-right ml-3 w-16">
          <div className="text-sm font-bold text-gray-700 dark:text-gray-200">{voteCount} vote{voteCount !== 1 ? 's' : ''}</div>
          {totalVotes > 0 && (
            <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-1.5 mt-1">
              <div
                className="bg-nba-blue h-1.5 rounded-full"
                style={{ width: `${(voteCount / totalVotes) * 100}%` }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Award Card
// ─────────────────────────────────────────────
function AwardCard({ category, data, seasonId, isAdmin, isGM, userTeamId, onRefresh, totalGMs }) {
  const meta = CATEGORY_META[category];
  const [showAnnounce, setShowAnnounce] = useState(false);
  const [showVote, setShowVote] = useState(false);
  const [showNomineePicker, setShowNomineePicker] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  if (!data) return null;

  const { eligible, reason, votingStatus, winner, myVote, voteSummary, voteCount = 0, nominees = [], candidates = [] } = data;
  const totalVotes = voteSummary ? Object.values(voteSummary).reduce((a, b) => a + b, 0) : 0;
  const hasVoted = myVote !== null && myVote !== undefined;
  // During voting, show nominees if set; otherwise fall back to full candidate list
  const ballotList = (votingStatus === 'open' || votingStatus === 'announced') && nominees.length > 0
    ? nominees
    : candidates;

  const handleAnnounce = async (winnerId, winnerType, notes) => {
    setLoading(true); setError(null);
    try {
      await awardsAPI.announce({ seasonId, category, winnerId, winnerType, notes });
      setShowAnnounce(false);
      await onRefresh();
    } catch (e) { setError(e.response?.data?.error || 'Failed to announce'); }
    finally { setLoading(false); }
  };

  const handleCloseVoting = async () => {
    setLoading(true); setError(null);
    try {
      await awardsAPI.closeVoting({ seasonId, category });
      setShowAnnounce(false);
      await onRefresh();
    } catch (e) { setError(e.response?.data?.error || 'Failed to close voting'); }
    finally { setLoading(false); }
  };

  const handleVote = async (nomineeId, nomineeType) => {
    setLoading(true); setError(null);
    try {
      await awardsAPI.vote({ seasonId, category, nomineeId, nomineeType });
      setShowVote(false);
      await onRefresh();
    } catch (e) { setError(e.response?.data?.error || 'Failed to submit vote'); }
    finally { setLoading(false); }
  };

  return (
    <>
      <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg dark:shadow-gray-900 overflow-hidden
        ${!eligible ? 'opacity-60' : ''}`}>

        {/* Card header */}
        <div className={`px-5 py-4 flex items-center justify-between
          ${votingStatus === 'open' ? 'bg-gradient-to-r from-blue-600 to-blue-700' :
            votingStatus === 'announced' ? 'bg-gradient-to-r from-yellow-500 to-amber-500' :
            'bg-gradient-to-r from-nba-blue to-nba-red'}`}>
          <div className="flex items-center gap-2">
            <span className="text-2xl">{meta.emoji}</span>
            <div>
              <div className="text-white font-bold text-lg leading-tight">{meta.label}</div>
              {votingStatus === 'open' && (
                <div className="text-blue-100 text-xs flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block animate-pulse" />
                  Voting open
                </div>
              )}
              {votingStatus === 'announced' && (
                <div className="text-yellow-100 text-xs">🏅 Winner announced</div>
              )}
            </div>
          </div>
          <span className="text-white/70 font-bold text-sm">{meta.short}</span>
        </div>

        <div className="p-5">
          {/* Not eligible */}
          {!eligible && (
            <div className="text-center py-6 text-gray-500 dark:text-gray-400">
              <div className="text-3xl mb-2">⏳</div>
              <p className="text-sm">{reason}</p>
            </div>
          )}

          {eligible && (
            <>
              {/* Winner spotlight */}
              {winner && <WinnerSpotlight winner={winner} candidates={candidates} category={category} />}

              {/* Candidates */}
              {candidates.length > 0 ? (
                <div className="space-y-1">
                  {candidates.map((c, i) => (
                    <CandidateRow
                      key={candidateId(c, category)}
                      candidate={c}
                      category={category}
                      rank={i + 1}
                      voteSummary={voteSummary}
                      totalVotes={totalVotes}
                      myVote={myVote}
                      votingStatus={votingStatus || 'closed'}
                      isWinner={winner && (candidateId(c, category) === winner.id || candidateId(c, category) === parseInt(winner.id))}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-4">
                  No eligible candidates yet (min. 10 games required)
                </p>
              )}

              {error && <p className="text-red-500 text-sm mt-3">{error}</p>}

              {/* ── GM voting controls ── */}
              {isGM && votingStatus === 'open' && (
                <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                  {hasVoted ? (
                    <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 font-semibold">
                      <span>✓</span> Vote submitted — results revealed when voting closes.
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowVote(true)}
                      disabled={loading}
                      className="w-full py-2 rounded-lg bg-nba-blue hover:bg-blue-800 text-white font-semibold transition text-sm">
                      🗳️ Cast Your Vote
                    </button>
                  )}
                </div>
              )}

              {/* ── Admin live vote totals (open voting) ── */}
              {isAdmin && votingStatus === 'open' && voteSummary && (
                <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                  <div className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                    Live vote totals — {voteCount} of {totalGMs} GMs voted
                  </div>
                  <div className="space-y-1.5">
                    {ballotList.map(n => {
                      const id = category === 'gmoy' ? n.userId : n.playerId;
                      const name = category === 'gmoy' ? gmDisplayName(n, { preferFirstName: true }) : n.player;
                      const votes = voteSummary[id] || 0;
                      const pct = voteCount > 0 ? Math.round((votes / voteCount) * 100) : 0;
                      return (
                        <div key={id} className="flex items-center gap-2">
                          <div className="w-28 text-xs text-gray-600 dark:text-gray-300 truncate">{name}</div>
                          <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full h-2">
                            <div className="bg-nba-blue h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
                          </div>
                          <div className="text-xs font-bold text-gray-700 dark:text-gray-200 w-10 text-right">{votes}v</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── Admin controls ── */}
              {isAdmin && (
                <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 flex flex-wrap gap-2">
                  {votingStatus !== 'open' && votingStatus !== 'announced' && (
                    <button onClick={() => setShowNomineePicker(true)} disabled={loading}
                      className="flex-1 py-1.5 px-3 rounded-lg border border-blue-500 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 text-sm font-semibold transition">
                      🗳️ Set Nominees & Open Voting
                    </button>
                  )}
                  {votingStatus === 'open' && (
                    <>
                      <button onClick={() => setShowNomineePicker(true)} disabled={loading}
                        className="flex-1 py-1.5 px-3 rounded-lg border border-blue-400 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 text-sm transition">
                        ✏️ Edit Nominees
                      </button>
                      <button onClick={handleCloseVoting} disabled={loading}
                        className="flex-1 py-1.5 px-3 rounded-lg border border-gray-400 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm transition">
                        🔒 Close Voting
                      </button>
                    </>
                  )}
                  {votingStatus !== 'announced' && (
                    <button onClick={() => setShowAnnounce(true)} disabled={loading}
                      className="flex-1 py-1.5 px-3 rounded-lg bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-semibold transition">
                      🏅 Announce Winner
                    </button>
                  )}
                  {votingStatus === 'announced' && (
                    <button onClick={() => setShowAnnounce(true)} disabled={loading}
                      className="flex-1 py-1.5 px-3 rounded-lg border border-yellow-500 text-yellow-600 dark:text-yellow-400 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 text-sm transition">
                      ✏️ Change Winner
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modals */}
      {showNomineePicker && (
        <NomineePickerModal
          category={category}
          candidates={candidates}
          seasonId={seasonId}
          onClose={() => setShowNomineePicker(false)}
          onRefresh={onRefresh}
        />
      )}
      {showAnnounce && (
        <AnnounceModal
          category={category}
          candidates={ballotList.length > 0 ? ballotList : candidates}
          onConfirm={handleAnnounce}
          onClose={() => setShowAnnounce(false)}
        />
      )}
      {showVote && (
        <VoteModal
          category={category}
          candidates={ballotList.length > 0 ? ballotList : candidates}
          userTeamId={userTeamId}
          onConfirm={handleVote}
          onClose={() => setShowVote(false)}
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────────
// Past Winners Section
// ─────────────────────────────────────────────
const AWARD_META_FULL = {
  mvp:  { emoji: '🏆', label: 'MVP' },
  dpoy: { emoji: '🛡️', label: 'Best Defender' },
  gmoy: { emoji: '🧠', label: 'Coach of the Year' },
  mip:  { emoji: '📈', label: 'Most Improved' },
  roty: { emoji: '🌟', label: 'Rookie of the Year' },
};

function PastWinnersSection() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    awardsAPI.getHistory()
      .then(res => setHistory(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!history.length) return null;

  return (
    <div className="mt-12">
      <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-6">🏆 Award Winners History</h2>
      <div className="space-y-8">
        {history.map(season => (
          <div key={season.seasonId} className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
            <div className="px-5 py-3 bg-gradient-to-r from-nba-blue to-nba-red">
              <h3 className="text-white font-bold text-lg">{season.seasonName}</h3>
            </div>
            <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {season.awards.map(award => {
                const meta = AWARD_META_FULL[award.category] || { emoji: '🏅', label: award.category };
                return (
                  <div key={award.id}
                    className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-100 dark:border-gray-700">
                    <div className="text-2xl shrink-0">{meta.emoji}</div>
                    <div className="min-w-0">
                      <div className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">{meta.label}</div>
                      {award.imageUrl && (
                        <img src={award.imageUrl} alt={award.winnerName}
                          className="w-8 h-8 rounded-full object-cover my-1 bg-gray-200"
                          onError={e => { e.target.style.display = 'none'; }} />
                      )}
                      <div className="text-sm font-bold text-gray-800 dark:text-gray-100 truncate">{award.winnerName || '—'}</div>
                      {award.team && <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{award.team}</div>}
                      {award.notes && <div className="text-xs italic text-gray-400 dark:text-gray-500 mt-0.5 truncate">"{award.notes}"</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────
const Awards = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isAdmin = isAuthenticated && user?.role === 'admin';
  const isGM    = isAuthenticated && user?.role === 'team_owner';

  const load = useCallback(async () => {
    try {
      const res = await awardsAPI.getCandidates();
      setData(res.data);
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Failed to load awards data.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="text-2xl text-gray-600 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-16 max-w-lg text-center">
        <div className="text-5xl mb-4">🏅</div>
        <h1 className="text-2xl font-bold mb-2 text-gray-800 dark:text-gray-100">Season Awards</h1>
        <p className="text-gray-500 dark:text-gray-400 mb-8">{error}</p>
        <PastWinnersSection />
      </div>
    );
  }

  // ── Locked state (not enough games yet) ──
  if (!data?.isUnlocked) {
    const played = data?.gamesPlayed ?? 0;
    const pct = Math.min((played / 100) * 100, 100);
    return (
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        {data?.isOffseason && (
          <div className="mb-6 px-4 py-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 text-amber-800 dark:text-amber-300 text-sm font-medium">
            ⏸️ No active season — showing <strong>{data.season?.name}</strong>
          </div>
        )}
        <div className="text-center py-8">
          <div className="text-6xl mb-4">🏅</div>
          <h1 className="text-3xl font-bold mb-3 text-gray-800 dark:text-gray-100">Season Awards</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-8">
            Award candidates are revealed after <strong>100 league games</strong> have been played.
          </p>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
            <div className="flex justify-between text-sm font-semibold mb-2 text-gray-600 dark:text-gray-400">
              <span>{played} games played</span>
              <span>100 needed</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4">
              <div
                className="bg-gradient-to-r from-nba-blue to-nba-red h-4 rounded-full transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">{100 - played} more games to go</p>
          </div>
        </div>
        <PastWinnersSection />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Off-season banner */}
      {data?.isOffseason && (
        <div className="mb-6 px-4 py-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 text-amber-800 dark:text-amber-300 text-sm font-medium">
          ⏸️ No active season — showing <strong>{data.season?.name}</strong>
          {isAdmin && <span className="ml-2 opacity-70">You can still announce winners below.</span>}
        </div>
      )}

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-800 dark:text-gray-100">
          🏅 Season Awards
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          {data.season?.name} · {data.gamesPlayed} regular season games played
        </p>
        {isGM && !data.isOffseason && (
          <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
            You can vote in any category where voting is open. You cannot vote for your own team's players.
          </p>
        )}
      </div>

      {/* Stat Leaders */}
      <StatLeadersSection
        statLeaders={data.statLeaders}
        playoffsStarted={data.playoffsStarted}
        statLeadersLocked={data.statLeadersLocked}
        isAdmin={isAdmin}
        seasonId={data.season?.id}
        onRefresh={load}
      />

      {/* Award cards grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {CATEGORIES.map(cat => (
          <div key={cat} className={cat === 'gmoy' ? 'lg:col-span-2 xl:col-span-1' : ''}>
            <AwardCard
              category={cat}
              data={data.awards?.[cat]}
              seasonId={data.season?.id}
              isAdmin={isAdmin}
              isGM={isGM && !data.isOffseason}
              userTeamId={user?.teamId}
              onRefresh={load}
              totalGMs={data.totalGMs || 0}
            />
          </div>
        ))}
      </div>

      {/* Past Winners History */}
      <PastWinnersSection />
    </div>
  );
};

export default Awards;
